import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaCashRegister, FaUsers, FaMobile, FaGift, FaCreditCard, FaChartLine,
  FaStopwatch, FaPercentage, FaCheckCircle, FaDownload, FaStar, 
  FaArrowRight, FaCog, FaStore, FaUtensils, FaReceipt, FaEye,
  FaTimes, FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import './FastFoodPosPage.css';

const FastFoodPosPage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Modal state management
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const galleryImages = [
    '/kiosk/kiosk1.png',
    '/kiosk/kiosk2.png'
  ];

  const openModal = (imageSrc) => {
    const index = galleryImages.indexOf(imageSrc);
    setCurrentImageIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const features = [
    {
      icon: FaCashRegister,
      title: 'Klasická pokladňa',
      description: 'Tradičná pokladňa s plnou funkčnosťou pre personál reštaurácie s intuitívnym rozhraním'
    },
    {
      icon: FaMobile,
      title: 'Kiosková pokladňa',
      description: 'Samoobslužná kiosková pokladňa pre zákazníkov s jednoduchým ovládaním a moderným dizajnom'
    },
    {
      icon: FaGift,
      title: 'Vernostný program',
      description: 'Integrovaný vernostný program s bodmi, zľavami a špeciálnymi ponukami pre stálych zákazníkov'
    },
    {
      icon: FaCreditCard,
      title: 'Platobné terminály',
      description: 'Bezproblémová integrácia s platobnými terminálmi pre rýchle a bezpečné platby'
    },
    {
      icon: FaUsers,
      title: 'Správa zákazníkov',
      description: 'Komplexná databáza zákazníkov s históriou objednávok a personalizovanými ponukami'
    },
    {
      icon: FaChartLine,
      title: 'Reportovanie',
      description: 'Pokročilé reporty a analytika pre lepšie pochopenie prevádzky a zákazníckych potrieb'
    }
  ];

  const benefits = [
    {
      icon: FaStopwatch,
      title: 'Rýchlosť obsluhy',
      value: '50%',
      description: 'Zrýchlenie času obsluhy zákazníka'
    },
    {
      icon: FaPercentage,
      title: 'Zvýšenie tržieb',
      value: '30%',
      description: 'Nárast tržieb vďaka efektívnejšej obsluhe'
    },
    {
      icon: FaCheckCircle,
      title: 'Presnosť objednávok',
      value: '99%',
      description: 'Eliminovanie chýb v objednávkach'
    },
    {
      icon: FaUsers,
      title: 'Spokojnosť zákazníkov',
      value: '95%',
      description: 'Vysoká úroveň spokojnosti zákazníkov'
    }
  ];

  const industries = [
    {
      icon: FaUtensils,
      title: 'Fast food reštaurácie',
      description: 'Optimalizované pre rýchle občerstvenie a fastfood prevádzky'
    },
    {
      icon: FaStore,
      title: 'Reštauračné reťazce',
      description: 'Centralizované riadenie viacerých pobočiek s jednotným systémom'
    },
    {
      icon: FaCashRegister,
      title: 'Kaviarne a bistrá',
      description: 'Jednoduché riešenie pre menšie gastro prevádzky'
    },
    {
      icon: FaReceipt,
      title: 'Delivery služby',
      description: 'Integrácia s rozvozovými službami a online objednávkami'
    }
  ];

  const testimonials = [
    {
      name: 'Tomáš Svoboda',
      company: 'Burger King Bratislava',
      text: 'FastFood POS nám výrazně zrýchlil obsluhu zákazníkov. Kiosková pokladňa je hit, zákazníci ju milujú a my máme viac času na prípravu jedla.',
      rating: 5
    },
    {
      name: 'Monika Hrušková',
      company: 'Quick Meal reťazec',
      text: 'Vernostný program zvýšil návratnosť našich zákazníkov o 40%. Systém je jednoduchý na ovládanie a personál sa ho naučil za jeden deň.',
      rating: 5
    }
  ];

  return (
    <div className="fastfoodpos-page">
      {/* Hero Section */}
      <section className="fastfoodpos-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1>FastFood POS</h1>
              <h2>Pokladničný systém pre reštaurácie</h2>
              <p>
                Flexibilný pokladničný systém určený pre reštauračné pobočky, ktorý spája jednoduchosť obsluhy, 
                moderný dizajn a maximálnu efektivitu pri práci so zákazníkom. Systém umožňuje nasadenie dvoch 
                typov pokladní – klasickej a kioskovej. Súčasťou je aj vernostný program a integrácia s platobnými terminálmi.
              </p>
              <div className="hero-actions">
                <Link to="/kontakt" className="btn btn-primary">
                  <FaArrowRight /> Získať ponuku
                </Link>
              </div>
            </div>
            <div className="hero-image">
              <img src="/fastfoodpos2.png" alt="FastFood POS" className="hero-product-image" />
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="gallery-section">
        <div className="container">
          <h2>Ukážky systému</h2>
          <div className="gallery-grid">
            <div className="gallery-item" onClick={() => openModal('/kiosk/kiosk1.png')}>
              <img src="/kiosk/kiosk1.png" alt="FastFood POS - ukážka 1" />
            </div>
            <div className="gallery-item" onClick={() => openModal('/kiosk/kiosk2.png')}>
              <img src="/kiosk/kiosk2.png" alt="FastFood POS - ukážka 2" />
            </div>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="technologies-section">
        <div className="container">
          <h2>Použité technológie</h2>
          <div className="technologies-grid">
            <div className="technology-card">
              <div className="tech-name">C#</div>
            </div>
            <div className="technology-card">
              <div className="tech-name">WPF</div>
            </div>
            <div className="technology-card">
              <div className="tech-name">SQL</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <h2>Kľúčové funkcie</h2>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">
                  <feature.icon />
                </div>
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <h2>Merateľné výsledky</h2>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">
                  <benefit.icon />
                </div>
                <div className="benefit-value">{benefit.value}</div>
                <h4>{benefit.title}</h4>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="industries-section">
        <div className="container">
          <h2>Ideálne pre</h2>
          <div className="industries-grid">
            {industries.map((industry, index) => (
              <div key={index} className="industry-card">
                <div className="industry-icon">
                  <industry.icon />
                </div>
                <h4>{industry.title}</h4>
                <p>{industry.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customers Section */}
      <section className="customers-section">
        <div className="container">
          <h2>Naši klienti</h2>
          <div className="customers-grid">
            <a 
              href="https://www.bb.cz/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="customer-card"
            >
              <img src="/bb_logo.jpg" alt="Bageterie Boulevard" />
              <h3>Bageterie Boulevard</h3>
              <p>Sieť reštaurácií s čerstvými bagetami</p>
            </a>
            <a 
              href="https://360pizza.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="customer-card"
            >
              <img src="/360pizza_logo.png" alt="360 Pizza" />
              <h3>360 Pizza</h3>
              <p>Moderná pizzéria s inovatívnym prístupom k príprave pizze</p>
            </a>
          </div>
        </div>
      </section>

      {/* Image Modal */}
      {isModalOpen && (
        <div className="image-modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <FaTimes />
            </button>
            <button className="modal-prev" onClick={prevImage}>
              <FaChevronLeft />
            </button>
            <img 
              src={galleryImages[currentImageIndex]} 
              alt={`FastFood POS - ukážka ${currentImageIndex + 1}`}
            />
            <button className="modal-next" onClick={nextImage}>
              <FaChevronRight />
            </button>
            <div className="modal-counter">
              {currentImageIndex + 1} / {galleryImages.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FastFoodPosPage;